import { baseDesignWidth } from '@/lib/consts'

export function leadZeros ( num, maxZeros = 1 ) {
  return '0'.repeat( Math.max( 0, maxZeros - (num.toString().length - 1) ) ) + num
}

export function randIntInc ( min, max ) {
  return Math.floor( Math.random() * (max - min + 1) ) + min
}

export function randInt ( min, max ) {
  return Math.floor( Math.random() * (max - min) ) + min
}

export function hexToRgb ( hex ) {
  let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec( hex )
  return result ? {
    r: parseInt( result[ 1 ], 16 ),
    g: parseInt( result[ 2 ], 16 ),
    b: parseInt( result[ 3 ], 16 )
  } : null
}

function typeCheck ( type, fn, testArg ) {
  /* eslint-disable */
  if ( typeof testArg !== type || typeof testArg === 'undefined' ) {
    console.error( `${fn} requires argument to be of type "${type}"! Received type ${typeof testArg}` )
    return false
  }
  return true
  /* eslint-enable */
}

export function rgbToHex ( r, g, b ) {
  if ( !typeCheck( 'number', 'rgbToHex', r ) ) return
  return '#' + leadZeros( r.toString( 16 ) ) + leadZeros( g.toString( 16 ) ) + leadZeros( b.toString( 16 ) )
}

export function darken ( col, amt ) {
  if ( !typeCheck( 'string', 'darken', col ) ) return
  let c = col.indexOf( '#' ) > -1
    ? Object.values( hexToRgb( col ) ).map( val => Math.round( val * (1 - amt) ) )
    : col.match( /\d+/g ).map( val => Math.round( parseInt( val, 10 ) * (1 - amt) ) )

  return rgbToHex( ...c )
}

export function lighten ( col, amt ) {
  if ( !typeCheck( 'string', 'lighten', col ) ) return
  let c = col.indexOf( '#' ) > -1
    ? Object.values( hexToRgb( col ) ).map( val => Math.round( Math.min( 255, val * (1 + amt) / 100 ) ) )
    : col.match( /\d+/g ).map( val => Math.round( Math.min( 255, parseInt( val, 10 ) * (1 + amt) / 100 ) ) )

  return rgbToHex( ...c )
}

export function hexToRgba ( hex, a = 1 ) {
  return 'rgba(' + Object.values( hexToRgb( hex ) ).join( ', ' ) + ', ' + a + ')'
}

export function isDark ( col ) {
  if ( col.indexOf( '#' ) > -1 ) col = Object.values( hexToRgb( col ) ).join( ',' )

  let lumArr = col.match( /([0-9]+)/g ).map( val => {
    let c = parseInt( val, 10 ) / 255
    return (c <= 0.03928) ? c / 12.92 : Math.pow( ((c + 0.055) / 1.055), 2.4 )
  } )

  let lum = 0.2126 * lumArr[ 0 ] + 0.7152 * lumArr[ 1 ] + 0.0722 * lumArr[ 2 ]

  return (lum < 0.5)
}

export function viewportScale ( px ) {
  return ((px / baseDesignWidth) * 100) + 'vw'
}

export function uuid ( a ) {
  // credit: https://gist.github.com/jed/982883
  return a ? (a ^ Math.random() * 16 >> a / 4).toString( 16 ) : ([ 1e7 ] + -1e3 + -4e3 + -8e3 + -1e11).replace( /[018]/g, uuid )
}

export function gradPtColor ( color1, color2, pt1 ) {
  // credit: adapted from https://stackoverflow.com/a/30144587
  if ( typeof color1 !== 'string' || color1.indexOf( '#' ) === -1 ) {
    console.error( `gradPointColor requires hex color values! Received color1: ${color1}, color2: ${color2}` )
    return
  }

  let col1 = hexToRgb( color1 )
  let col2 = hexToRgb( color2 )
  let pt2 = 1 - pt1
  let rgb = [
    Math.round( col1.r * pt1 + col2.r * pt2 ),
    Math.round( col1.g * pt1 + col2.g * pt2 ),
    Math.round( col1.b * pt1 + col2.b * pt2 )
  ]

  return rgbToHex( ...rgb )
}

export function nearestPow2 ( aSize ) {
  return Math.pow( 2, Math.round( Math.log( aSize ) / Math.log( 2 ) ) )
}

export function shuffleArr ( arr ) {
  // Durstenfeld/Fisher–Yates shuffle
  let array = arr.slice( 0 )
  for ( let i = array.length - 1; i > 0; i-- ) {
    let j = Math.floor( Math.random() * (i + 1) )
    let temp = array[ i ]
    array[ i ] = array[ j ]
    array[ j ] = temp
  }
  return array
}

/*
* indefinite-article.js v1.0.0, 12-18-2011
*
* @author: Rodrigo Neri (@rigoneri)
*
* (The MIT License)
*
* Permission is hereby granted, free of charge, to any person obtaining a copy
* of this software and associated documentation files (the "Software"), to deal
* in the Software without restriction, including without limitation the rights
* to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
* copies of the Software, and to permit persons to whom the Software is
* furnished to do so, subject to the following conditions:
*
* The above copyright notice and this permission notice shall be included in
* all copies or substantial portions of the Software.
*
* THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
* IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
* FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
* AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
* LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
* OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
* THE SOFTWARE.
*
* Modified and ported to es6 by Kyle @WLS
*/

export function indefiniteArticle ( phrase ) {
  // Getting the first word
  let match = /\w+/.exec( phrase )
  let word
  if ( match ) {
    word = match[ 0 ]
  } else {
    return 'an'
  }

  let lWord = word.toLowerCase()

  // Single letter word which should be preceeded by 'an'
  if ( lWord.length === 1 ) {
    if ( 'aedhilmnorsx'.indexOf( lWord ) >= 0 ) {
      return 'an'
    } else {
      return 'a'
    }
  }

  // Capital words which should likely be preceeded by 'an'
  if ( word.match( /(?!FJO|[HLMNS]Y.|RY[EO]|SQU|(F[LR]?|[HL]|MN?|N|RH?|S[CHKLMNPTVW]?|X(YL)?)[AEIOU])[FHLMNRSX][A-Z]/ ) ) {
    return 'an'
  }

  // Special cases where a word that begins with a vowel should be preceeded by 'a'
  let regexes = [ /^e[uw]/, /^onc?e\b/, /^uni([^nmd]|mo)/, /^u[bcfhjkqrst][aeiou]/ ]
  for ( let i in regexes ) {
    if ( lWord.match( regexes[ i ] ) ) {
      return 'a'
    }
  }

  // Special capital words (UK, UN)
  if ( word.match( /^U[NK][AIEO]/ ) ) {
    return 'a'
  } else if ( word === word.toUpperCase() ) {
    if ( 'aedhilmnorsx'.indexOf( lWord[ 0 ] ) >= 0 ) {
      return 'an'
    } else {
      return 'a'
    }
  }

  // Basic method of words that begin with a vowel being preceeded by 'an'
  if ( 'aeiou'.indexOf( lWord[ 0 ] ) >= 0 ) {
    return 'an'
  }

  // Instances where y follwed by specific letters is preceeded by 'an'
  if ( lWord.match( /^y(b[lor]|cl[ea]|fere|gg|p[ios]|rou|tt)/ ) ) {
    return 'an'
  }

  return 'a'
}

// https://stackoverflow.com/questions/5560248/programmatically-lighten-or-darken-a-hex-color-or-rgb-and-blend-colors
let _sbcRip = d => {
  let l = d.length
  let RGB = {}
  if ( l > 9 ) {
    d = d.split( ',' )
    if ( d.length < 3 || d.length > 4 ) return null
    RGB[ 0 ] = parseInt( d[ 0 ].slice( 4 ) )
    RGB[ 1 ] = parseInt( d[ 1 ] )
    RGB[ 2 ] = parseInt( d[ 2 ] )
    RGB[ 3 ] = d[ 3 ] ? parseFloat( d[ 3 ] ) : -1
  } else {
    if ( l === 8 || l === 6 || l < 4 ) return null
    if ( l < 6 ) {
      d = '#' + d[ 1 ] + d[ 1 ] + d[ 2 ] + d[ 2 ] + d[ 3 ] + d[ 3 ] + (l > 4 ? d[ 4 ] + '' + d[ 4 ] : '')
    }
    d = parseInt( d.slice( 1 ), 16 )
    RGB[ 0 ] = d >> 16 & 255
    RGB[ 1 ] = d >> 8 & 255
    RGB[ 2 ] = d & 255
    RGB[ 3 ] = l === 9 || l === 5 ? Math.round( ((d >> 24 & 255) / 255) * 10000 ) / 10000 : -1
  }
  return RGB
}

export function shadeBlendConvert ( p, from, to ) {
  if ( typeof p !== 'number' ||
    p < -1 ||
    p > 1 ||
    typeof from !== 'string' ||
    (from[ 0 ] !== 'r' && from[ 0 ] !== '#') ||
    (typeof to !== 'string' && typeof to !== 'undefined')
  ) return null

  let r = Math.round
  let h = from.length > 9
  h = typeof to === 'string' ? to.length > 9 ? true : to === 'c' ? !h : false : h
  let b = p < 0
  p = b ? p * -1 : p
  to = to && to !== 'c' ? to : b ? '#000000' : '#FFFFFF'
  let f = _sbcRip( from )
  let t = _sbcRip( to )
  if ( !f || !t ) return null
  if ( h ) {
    return 'rgb(' +
      r( (t[ 0 ] - f[ 0 ]) * p + f[ 0 ] ) + ',' +
      r( (t[ 1 ] - f[ 1 ]) * p + f[ 1 ] ) + ',' +
      r( (t[ 2 ] - f[ 2 ]) * p + f[ 2 ] ) +
      (f[ 3 ] < 0 && t[ 3 ] < 0
          ? ')'
          : ',' + (f[ 3 ] > -1 && t[ 3 ] > -1
          ? r( ((t[ 3 ] - f[ 3 ]) * p + f[ 3 ]) * 10000 ) / 10000
          : t[ 3 ] < 0 ? f[ 3 ] : t[ 3 ]) + ')'
      )
  } else {
    return '#' + (0x100000000 +
      (f[ 3 ] > -1 && t[ 3 ] > -1
          ? r( ((t[ 3 ] - f[ 3 ]) * p + f[ 3 ]) * 255 )
          : t[ 3 ] > -1
            ? r( t[ 3 ] * 255 )
            : f[ 3 ] > -1
              ? r( f[ 3 ] * 255 )
              : 255
      ) * 0x1000000 +
      r( (t[ 0 ] - f[ 0 ]) * p + f[ 0 ] ) * 0x10000 +
      r( (t[ 1 ] - f[ 1 ]) * p + f[ 1 ] ) * 0x100 +
      r( (t[ 2 ] - f[ 2 ]) * p + f[ 2 ] )).toString( 16 ).slice(
      f[ 3 ] > -1 || t[ 3 ] > -1
        ? 1
        : 3
    )
  }
}

function preventDefault ( e ) {
  e = e || window.event
  if ( e.preventDefault ) e.preventDefault()
  e.returnValue = false
}

let keys = { 37: 1, 38: 1, 39: 1, 40: 1 }
function preventDefaultForScrollKeys ( e ) {
  if ( keys[ e.keyCode ] ) {
    preventDefault( e )
    return false
  }
}

export function disableScroll () {
  window.addEventListener( 'DOMMouseScroll', preventDefault, false )
  window.onwheel = preventDefault
  window.onmousewheel = document.onmousewheel = preventDefault
  window.ontouchmove = preventDefault
  document.onkeydown = preventDefaultForScrollKeys
}

export function enableScroll () {
  window.removeEventListener( 'DOMMouseScroll', preventDefault, false )
  window.onmousewheel = document.onmousewheel = null
  window.onwheel = null
  window.ontouchmove = null
  document.onkeydown = null
}

export function storageAvailable ( type ) {
  try {
    let storage = window[type]
    let x = '__storage_test__'
    storage.setItem( x, x )
    storage.removeItem( x )
    return true
  } catch ( e ) {
    return false
  }
}



// WEBPACK FOOTER //
// ./src/lib/funcs.js